import { useState, type ReactNode } from 'react';
import { useClientStanding, useConfirmDischarge } from '../api/standing.ts';
import { useClientSummary } from '../api/navigation.ts';
import { LoadFailure } from './LoadFailure.tsx';
import { LivesAtLines } from './LivesAtLines.tsx';
import { formatDate } from '../dates.ts';
import { startStatement } from '../placementStart.ts';
import type {
  AlliedRole, CaseContact, ClientDischarge, ClientHold, HeldPower, PlacementStart, StandingAppointment, StandingVisits, TimelineEntry,
} from '../api/types.ts';

const CATEGORIES: { key: HeldPower['category']; label: string }[] = [
  { key: 'PERSONAL_NEEDS', label: 'Personal needs' },
  { key: 'PROPERTY_MANAGEMENT', label: 'Property' },
];

/**
 * What is true about this client now, above the history of how it came to be.
 *
 * Where they live and for how long, the authority the agency holds today, the
 * appointment it holds it under, and the people on the legal side of the case.
 * History — placements, stays, visits — is below it and not interleaved: a
 * case manager opening a record needs the current answer first, and a
 * timeline makes them reconstruct it.
 *
 * Authority is shown per side, and a side the agency does not hold is said in
 * words. A co-guardianship where another guardian holds the property alone
 * must not read as if the agency could sell the house.
 */
export function CurrentStanding({
  clientId,
  legalStatus,
  entries,
  placementStart,
}: {
  clientId: string;
  legalStatus: string | null;
  /** The timeline, once loaded. Where they live comes from its current placement. */
  entries: TimelineEntry[] | undefined;
  /** What can be said about when that placement began (092). */
  placementStart?: PlacementStart | null;
}) {
  const standing = useClientStanding(clientId);

  return (
    <section className="panel">
      <h2 className="panel-bar">
        Appointment and powers
      </h2>

      <LivesAt clientId={clientId} entries={entries} start={placementStart ?? null} />

      {standing.error && !standing.data ? (
        <LoadFailure
          what="This client's authority and contacts"
          error={standing.error}
          onRetry={() => void standing.refetch()}
          retrying={standing.isFetching}
          forbiddenIsSession={false}
        />
      ) : !standing.data ? (
        <p className="px-4 py-4 text-neutral-600">Loading authority and contacts…</p>
      ) : (
        <>
          {standing.data.discharge && <DischargeAssumed clientId={clientId} discharge={standing.data.discharge} />}
          {standing.data.hold && <HeldAtLoad hold={standing.data.hold} />}
          <VisitsThisYear visits={standing.data.visits} />
          <Authority
            appointments={standing.data.appointments}
            powers={standing.data.authority}
            legalStatus={legalStatus}
          />
          <People contacts={standing.data.contacts} />
        </>
      )}
    </section>
  );
}

/**
 * What the discharge closed, and whether anybody has confirmed it (107). Only
 * final accountings survive a discharge; the rest closed on an ASSUMPTION that
 * they were met or waived, and the page says so in those words until a person
 * confirms, by name and date. Never shown as complete: nobody has the evidence.
 */
function DischargeAssumed({ clientId, discharge }: { clientId: string; discharge: ClientDischarge }) {
  const confirm = useConfirmDischarge(clientId);
  const [note, setNote] = useState('');
  const when = discharge.discharged_on ? `on ${formatDate(discharge.discharged_on)}` : 'on a date not recorded';
  if (discharge.confirmed_on) {
    return (
      <Block title="Discharge confirmed">
        <p className="text-[0.9375rem] text-neutral-800">
          Discharged {when}. Confirmed by {discharge.confirmed_by} on {formatDate(discharge.confirmed_on)}.
        </p>
        {discharge.note && <p className="mt-0.5 text-sm text-neutral-600">{discharge.note}</p>}
      </Block>
    );
  }
  return (
    <Block title="Discharge awaiting confirmation">
      <p className="text-[0.9375rem] text-neutral-800">
        Discharged {when}. {discharge.closed_on_discharge === 0
          ? 'The discharge closed no obligations.'
          : `${discharge.closed_on_discharge} ${discharge.closed_on_discharge === 1 ? 'obligation' : 'obligations'} closed on the discharge; the obligations are assumed met or waived, pending confirmation.`}
        {' '}Final accountings stay open.
      </p>
      {discharge.may_confirm ? (
        <form
          className="mt-2 flex flex-wrap items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); confirm.mutate(note.trim() ? { note: note.trim() } : {}); }}
        >
          <label className="flex min-w-[16rem] flex-1 flex-col text-sm text-neutral-600">
            Note (optional)
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
                   className="mt-1 min-h-[44px] rounded-lg border border-neutral-300 px-3 text-[0.9375rem] text-neutral-900" />
          </label>
          <button type="submit" disabled={confirm.isPending}
                  className="min-h-[44px] rounded-lg bg-neutral-900 px-4 text-[0.9375rem] font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed">
            {confirm.isPending ? 'Confirming…' : 'Confirm, in my name'}
          </button>
          {confirm.error && <p role="alert" className="w-full text-sm text-red-700">{confirm.error.message}</p>}
        </form>
      ) : (
        <p className="mt-0.5 text-sm text-neutral-500">Compliance, or tiers 1 and 2, confirm a discharge.</p>
      )}
    </Block>
  );
}

/**
 * Held at load (102): the load could not appoint this client without a person
 * reading the court order. Said first and in words, because a held client
 * looks like a prospect otherwise, and a prospect is not waiting on anybody.
 */
function HeldAtLoad({ hold }: { hold: ClientHold }) {
  return (
    <Block title="Held at load">
      <p className="text-[0.9375rem] text-neutral-800">
        Waiting on a person to read the court order. Recording the appointment from the order ends the hold.
      </p>
      <p className="mt-0.5 text-sm text-neutral-600">{hold.reason}</p>
      <p className="mt-0.5 text-sm text-neutral-500">Held since {formatDate(hold.held_on)}</p>
    </Block>
  );
}

/**
 * This year's visits so far. The visits dashboard shows pace and priority, not
 * the counts, so the counts live on the client's own page. Nothing is shown for
 * a client who owes no visits by legal status.
 */
function VisitsThisYear({ visits }: { visits: StandingVisits | null }) {
  if (!visits) return null;
  return (
    <Block title="Visits this year">
      <p className="text-[0.9375rem] text-neutral-800 tabular-nums">
        {visits.visits_done} {visits.visits_done === 1 ? 'visit' : 'visits'} so far in {visits.visit_year}
        {visits.visits_needed > 0 ? `, of ${visits.visits_needed} owed for the year` : ', none owed'}
      </p>
      {visits.last_visit_date && (
        <p className="text-sm text-neutral-500">Last visit {formatDate(visits.last_visit_date)}</p>
      )}
    </Block>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-t border-neutral-200 px-4 py-3">
      <h3 className="text-sm font-bold text-neutral-800">{title}</h3>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/** "3 years, 2 months" from a day count PostgreSQL computed. */
function howLong(days: number): string {
  if (days < 31) return `${days} day${days === 1 ? '' : 's'}`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(days / 365.25);
  const rest = Math.floor((days - years * 365.25) / 30.44);
  return `${years} year${years === 1 ? '' : 's'}${rest > 0 ? `, ${rest} month${rest === 1 ? '' : 's'}` : ''}`;
}

/**
 * When the current placement began, as the record allows it to be said (092):
 * a recorded date with how long; "Here since before Allied's involvement", with
 * who asserted it, where the start is not a known date; "No move recorded since
 * Allied was appointed" where nothing is dated and nothing has moved. Never a
 * move-in date the record does not hold.
 */
function PlacementSince({ placement, start }: { placement: TimelineEntry; start: PlacementStart | null }) {
  const st = startStatement(start);
  const kind = placement.kind_label;
  if (st.kind === 'dated') {
    return (
      <span className="block text-sm text-neutral-600">
        Since {formatDate(st.since)}
        {placement.days !== null && ` · ${howLong(placement.days)}`}
        {kind && ` · ${kind}`}
      </span>
    );
  }
  if (st.kind === 'none') {
    return kind ? <span className="block text-sm text-neutral-600">{kind}</span> : null;
  }
  return (
    <span className="block text-sm text-neutral-600">
      {st.text}
      {kind && ` · ${kind}`}
    </span>
  );
}

function LivesAt({ clientId, entries, start }: { clientId: string; entries: TimelineEntry[] | undefined; start: PlacementStart | null }) {
  // A-#772 3 (A-#123): the address with its full town line, from the summary's lives_at, as the Client card shows it.
  // Same query key as the card's, so it is usually answered already. Until it answers, or if it has no residence,
  // the timeline's location and city, as before.
  const summary = useClientSummary(clientId);
  // Nothing until the timeline has answered: the header already says where to
  // find them, and "not recorded" before an answer would be a false statement.
  if (!entries) return null;
  const placement = entries.find((e) => e.entry_kind === 'Placement' && e.is_current);
  const stay = entries.find((e) => e.entry_kind === 'Stay' && e.is_current);
  const at = summary.data?.lives_at ?? null;

  return (
    <Block title="Lives at">
      {placement ? (
        <p className="text-[0.9375rem] text-neutral-800">
          {at ? (
            <span className="font-medium"><LivesAtLines at={at} showAway={false} /></span>
          ) : (
            <>
              <span className="font-medium">{placement.location ?? placement.kind_label ?? 'A placement'}</span>
              {placement.city && <span className="text-neutral-600">, {placement.city}</span>}
            </>
          )}
          <PlacementSince placement={placement} start={start} />
        </p>
      ) : (
        <p className="text-[0.9375rem] text-neutral-600">No current placement recorded.</p>
      )}
      {stay && (
        <p className="mt-1.5 text-[0.9375rem] text-neutral-800">
          <span className="font-medium">Away: {stay.location ?? stay.kind_label ?? 'a temporary stay'}</span>
          <span className="block text-sm text-neutral-600">
            Since {stay.started_on ? formatDate(stay.started_on) : 'an unrecorded date'}
            {stay.bed_held ? ' · placement held' : ''}
            {stay.expected_return_on && ` · expected back ${formatDate(stay.expected_return_on)}`}
          </span>
        </p>
      )}
    </Block>
  );
}

function roleWords(role: AlliedRole): string {
  return role === 'SOLE' ? 'Sole guardian' : role === 'CO' ? 'Co-guardian' : 'Not held by the agency';
}

function Authority({
  appointments,
  powers,
  legalStatus,
}: {
  appointments: StandingAppointment[];
  powers: HeldPower[];
  legalStatus: string | null;
}) {
  /**
   * Not appointed means nothing is held, whatever the appointment row says.
   *
   * Migration 024 carried an appointment across for every client from the
   * client record, Pipeline clients included, with the scope and commission
   * date the record held — so a client pending appointment has a row naming the
   * agency sole guardian of both sides and a commission date in the future.
   * Shown as it stands, that is a statement of authority the agency does not
   * have. Until the row is corrected, the page says what is true.
   */
  if (legalStatus === 'Not appointed') {
    return (
      <Block title="Authority">
        <p className="text-[0.9375rem] text-neutral-800">
          Not appointed. The agency never held authority: another guardian was appointed, or the petition was
          denied or withdrawn.
        </p>
      </Block>
    );
  }
  if (legalStatus === 'Pipeline' || legalStatus === 'Held at load') {
    const index = appointments.find((a) => a.index_number)?.index_number;
    return (
      <Block title="Authority">
        <p className="text-[0.9375rem] text-neutral-800">
          {legalStatus === 'Held at load'
            ? 'No appointment on record until the order is read. The agency holds no authority on record.'
            : 'Pending appointment. The agency holds no authority until the court appoints it.'}
        </p>
        {index && (
          <p className="mt-0.5 text-sm text-neutral-500">
            Index number <span className="font-mono">{index}</span>
          </p>
        )}
      </Block>
    );
  }

  const live = appointments.filter((a) => a.is_live);
  const ended = appointments.length - live.length;

  if (appointments.length === 0) {
    return (
      <Block title="Authority">
        <p className="text-[0.9375rem] text-neutral-600">
          No appointment on file, so no authority is recorded.
        </p>
      </Block>
    );
  }

  return (
    <Block title="Authority">
      {live.length === 0 && (
        <p className="text-[0.9375rem] text-neutral-600">
          No live appointment. {ended} ended appointment{ended === 1 ? '' : 's'} on file.
        </p>
      )}

      {live.map((a) => (
        <div key={a.id} className="mb-3 last:mb-0">
          <p className="text-[0.9375rem] text-neutral-800">
            <span className="font-mono">{a.index_number ?? 'No index number'}</span>
            <span className="text-neutral-600">
              {' · '}
              {[
                a.is_temporary ? 'Temporary' : 'Permanent',
                a.is_successor && 'Successor',
                a.is_limited && 'Limited',
                a.is_trustee && 'Trustee',
              ].filter(Boolean).join(', ')}
              {a.commission_date ? ` · commissioned ${formatDate(a.commission_date)}` : ' · not yet commissioned'}
            </span>
          </p>
          {a.prior_index_numbers && a.prior_index_numbers.length > 0 && (
            <p className="text-sm text-neutral-500">
              Took over from {a.prior_index_numbers.join(', ')}
            </p>
          )}

          <dl className="mt-2 space-y-2">
            {CATEGORIES.map((c) => {
              const role = c.key === 'PERSONAL_NEEDS' ? a.allied_person_role : a.allied_property_role;
              // By appointment, never by index number: the same number is written
              // several ways, and a successor's powers come from another proceeding.
              //
              // One line per power. v_current_authority has a row per order that
              // grants it, so a power granted by the temporary order and again by
              // the permanent one arrives twice. It is one authority held: its
              // qualifiers and the orders it was granted by reference to are merged.
              const held = [
                ...powers
                  .filter((p) => p.category === c.key && p.appointment_id === a.id)
                  .reduce((byCode, p) => {
                    const line = byCode.get(p.power_code) ?? {
                      code: p.power_code, label: p.power_label,
                      qualifiers: new Set<string>(), references: new Set<string>(),
                    };
                    if (p.qualifier) line.qualifiers.add(p.qualifier);
                    for (const r of p.granted_by_reference_to ?? []) line.references.add(r.signed_date);
                    return byCode.set(p.power_code, line);
                  }, new Map<string, { code: string; label: string; qualifiers: Set<string>; references: Set<string> }>())
                  .values(),
              ];
              return (
                <div key={c.key}>
                  <dt className="text-sm text-neutral-500">
                    {c.label} ·{' '}
                    <span className={role ? 'font-semibold text-neutral-800' : 'font-semibold text-neutral-600'}>
                      {roleWords(role)}
                    </span>
                  </dt>
                  <dd>
                    {role === null ? null : held.length === 0 ? (
                      <p className="text-sm text-neutral-600">No powers recorded on the orders on file.</p>
                    ) : (
                      <ul className="mt-0.5 space-y-0.5">
                        {held.map((p) => (
                          <li key={p.code} className="text-[0.9375rem] text-neutral-800">
                            {p.label}
                            {p.qualifiers.size > 0 && (
                              <span className="text-neutral-600"> — {[...p.qualifiers].join('; ')}</span>
                            )}
                            {p.references.size > 0 && (
                              <span className="block text-xs text-neutral-500">
                                by reference to the order of{' '}
                                {[...p.references].sort().map((d) => formatDate(d)).join(', ')}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      ))}
    </Block>
  );
}

function People({ contacts }: { contacts: CaseContact[] }) {
  return (
    <Block title="On the case">
      {contacts.length === 0 ? (
        <p className="text-[0.9375rem] text-neutral-600">
          No co-guardian, evaluator, examiner or counsel recorded.
        </p>
      ) : (
        <ul className="space-y-2">
          {contacts.map((c) => (
            <li key={c.assignment_id} className="text-[0.9375rem] text-neutral-800">
              <span className="text-sm text-neutral-500">{c.role_label}</span>
              <span className="block font-medium">
                {c.name ?? 'Name not recorded'}
                {c.credentials && <span className="font-normal text-neutral-600">, {c.credentials}</span>}
              </span>
              {c.authority_note && <span className="block text-sm text-neutral-600">{c.authority_note}</span>}
              {c.do_not_contact && (
                <span className="block text-sm font-semibold text-neutral-900">
                  Do not contact{c.do_not_contact_note ? `: ${c.do_not_contact_note}` : ''}
                </span>
              )}
              {!c.do_not_contact && (c.phone || c.email) && (
                <span className="block text-sm text-neutral-600">
                  {[c.phone, c.email].filter(Boolean).join(' · ')}
                </span>
              )}
              {(c.appointed_date ?? c.started_on) && (
                <span className="block text-xs text-neutral-500">
                  Since {formatDate((c.appointed_date ?? c.started_on)!)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Block>
  );
}
